import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api.js';

export interface AgencyBranding {
  workspace_name: string;
  logo_url: string | null;
  dark_logo_url: string | null;
  favicon_url: string | null;
  updated_at?: string;
}

export const DEFAULT_BRANDING: AgencyBranding = {
  workspace_name: 'Techosolution',
  logo_url: null,
  dark_logo_url: null,
  favicon_url: null,
  updated_at: new Date().toISOString(),
};

interface BrandingContextType {
  branding: AgencyBranding;
  loading: boolean;
  updateBranding: (updates: Partial<AgencyBranding>) => Promise<AgencyBranding>;
  resetBranding: () => Promise<AgencyBranding>;
  refreshBranding: () => Promise<void>;
}

const BRANDING_CACHE_KEY = 'tsos_agency_branding_cache';

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export function applyDocumentTitle(workspaceName: string | null | undefined) {
  if (typeof document === 'undefined') return;
  const name = workspaceName && workspaceName.trim() !== '' ? workspaceName.trim() : 'Techosolution';
  document.title = `${name} Project OS`;
}

export function applyFavicon(faviconUrl: string | null) {
  if (typeof document === 'undefined') return;

  const targetHref = faviconUrl && faviconUrl.trim() !== '' ? faviconUrl : './icons/icon-192.png';

  // Determine appropriate mime type
  let mimeType = 'image/png';
  if (targetHref.startsWith('data:image/x-icon') || targetHref.includes('.ico')) {
    mimeType = 'image/x-icon';
  } else if (targetHref.startsWith('data:image/svg+xml') || targetHref.includes('.svg')) {
    mimeType = 'image/svg+xml';
  } else if (targetHref.startsWith('data:image/webp') || targetHref.includes('.webp')) {
    mimeType = 'image/webp';
  } else if (targetHref.startsWith('data:image/jpeg') || targetHref.includes('.jpg') || targetHref.includes('.jpeg')) {
    mimeType = 'image/jpeg';
  }

  // Cache busting for non-data and non-blob URLs
  const finalHref = targetHref.startsWith('data:') || targetHref.startsWith('blob:')
    ? targetHref
    : `${targetHref}${targetHref.includes('?') ? '&' : '?'}v=${Date.now()}`;

  // Update or create standard favicon link
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = mimeType;
  link.href = finalHref;

  // Update apple-touch-icon
  let appleLink = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null;
  if (!appleLink) {
    appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    document.head.appendChild(appleLink);
  }
  appleLink.href = finalHref;
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<AgencyBranding>(() => {
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem(BRANDING_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && typeof parsed === 'object') {
            return { ...DEFAULT_BRANDING, ...parsed };
          }
        }
      } catch {}
    }
    return DEFAULT_BRANDING;
  });

  const [loading, setLoading] = useState(false);

  // Apply favicon and document title on initial load and changes
  useEffect(() => {
    applyFavicon(branding.favicon_url);
    applyDocumentTitle(branding.workspace_name);
  }, [branding.favicon_url, branding.workspace_name]);

  const refreshBranding = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<any>('/api/v1/branding');
      const data = res?.data || res;
      if (data && typeof data === 'object') {
        const merged: AgencyBranding = {
          workspace_name: data.workspace_name || 'Techosolution',
          logo_url: data.logo_url ?? null,
          dark_logo_url: data.dark_logo_url ?? null,
          favicon_url: data.favicon_url ?? null,
          updated_at: data.updated_at || new Date().toISOString(),
        };
        setBranding(merged);
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(merged));
          } catch {}
        }
        applyFavicon(merged.favicon_url);
        applyDocumentTitle(merged.workspace_name);
      }
    } catch (err) {
      console.warn('Could not fetch workspace branding from server:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  const updateBranding = async (updates: Partial<AgencyBranding>): Promise<AgencyBranding> => {
    const payload = {
      ...branding,
      ...updates,
    };

    // Optimistic UI update
    setBranding(payload);
    applyFavicon(payload.favicon_url);
    applyDocumentTitle(payload.workspace_name);

    try {
      const res = await api.post<any>('/api/v1/branding', payload);
      const saved = res?.data || payload;
      const finalBranding: AgencyBranding = {
        workspace_name: saved.workspace_name || payload.workspace_name || 'Techosolution',
        logo_url: saved.logo_url !== undefined ? saved.logo_url : payload.logo_url,
        dark_logo_url: saved.dark_logo_url !== undefined ? saved.dark_logo_url : payload.dark_logo_url,
        favicon_url: saved.favicon_url !== undefined ? saved.favicon_url : payload.favicon_url,
        updated_at: saved.updated_at || new Date().toISOString(),
      };
      setBranding(finalBranding);
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(finalBranding));
        } catch {}
      }
      applyFavicon(finalBranding.favicon_url);
      applyDocumentTitle(finalBranding.workspace_name);
      return finalBranding;
    } catch (err) {
      // Revert if error
      refreshBranding();
      throw err;
    }
  };

  const resetBranding = async (): Promise<AgencyBranding> => {
    try {
      await api.post<any>('/api/v1/branding/reset');
    } catch (err) {
      // Even if reset endpoint fails, attempt direct POST with nulls
      try {
        await api.post<any>('/api/v1/branding', DEFAULT_BRANDING);
      } catch (inner) {
        console.warn('Reset endpoint failed:', inner);
      }
    }

    setBranding(DEFAULT_BRANDING);
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(BRANDING_CACHE_KEY);
      } catch {}
    }
    applyFavicon(null);
    applyDocumentTitle(DEFAULT_BRANDING.workspace_name);
    return DEFAULT_BRANDING;
  };

  return (
    <BrandingContext.Provider
      value={{
        branding,
        loading,
        updateBranding,
        resetBranding,
        refreshBranding,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}
